import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {
  App,
  areAppTypesEquivalent,
  DIPLOMATEN_CODE,
  ImmobilieFormatted,
} from '@ps20-frontend/frontend-models';
import { ImmobilienAuswahlModalText } from '@ps20-frontend/gui-components';
import {
  NxRadioChange,
  NxRadioGroupComponent,
  NxRadioComponent,
} from '@allianz/ng-aquila/radio-button';
import { Subscription } from 'rxjs';
import {
  NxDialogService,
  NxModalConfig,
  NxModalRef,
  NxModalState,
  NxModalContentDirective,
  NxModalActionsDirective,
  NxModalCloseDirective,
} from '@allianz/ng-aquila/modal';
import { select } from '@angular-redux3/store';
import {
  selectImmobilieAuswahlErrorExists,
  selectImmobilieRequestIsInProgress,
  selectIsAuslandsImmobilie,
  selectRequestFailed,
} from '@ps20-frontend/selector';
import { Observable } from 'redux';
import { NxHeadlineComponent } from '@allianz/ng-aquila/headline';
import { FormsModule } from '@angular/forms';
import { NxtFieldSetModule } from '@nxt/ngx-ndbx-extensions/fieldset';
import { NxErrorComponent } from '@allianz/ng-aquila/base';
import { NxMessageComponent } from '@allianz/ng-aquila/message';
import { NxSpinnerComponent } from '@allianz/ng-aquila/spinner';
import { NxButtonComponent } from '@allianz/ng-aquila/button';
import { AsyncPipe } from '@angular/common';
import { HtmlPipe } from '@ps20-frontend/text-service';
import { InfoIconComponent } from '../info-icon/info-icon.component';

const DEFAULT_IMMO_MODAL_ARIA_LABEL = 'Immobiliendialog';
const DEFAULT_IMMO_MODAL_CLOSE_BUTTON_LABEL = 'Immobiliendialog schließen';

@Component({
  selector: 'ps20-frontend-immobilien-auswahl',
  templateUrl: './immobilien-auswahl.component.html',
  styleUrls: ['./immobilien-auswahl.component.scss'],
  imports: [
    NxModalContentDirective,
    NxHeadlineComponent,
    NxRadioGroupComponent,
    FormsModule,
    NxtFieldSetModule,
    InfoIconComponent,
    NxRadioComponent,
    NxErrorComponent,
    NxMessageComponent,
    NxSpinnerComponent,
    NxModalActionsDirective,
    NxButtonComponent,
    NxModalCloseDirective,
    AsyncPipe,
    HtmlPipe,
  ],
})
export class ImmobilienAuswahlComponent implements OnDestroy {
  private readonly dialogService = inject(NxDialogService);

  @ViewChild('immoModal') templateRef: TemplateRef<unknown>;

  @Input() set open(value: boolean) {
    this.isOpen = this.showModal(value);
  }

  @Input() selectedImmobilieId: string;
  @Input() canSelectNeueImmobilie = true;
  @Input() immobilien: Array<ImmobilieFormatted> = [];
  @Input() text: ImmobilienAuswahlModalText;
  @Input() kooperation: string;
  @Input() openForApp: App;

  @Output() immobilieAusgewaehlt = new EventEmitter<NxRadioChange>();
  @Output() neueImmobilieAusgewaehlt = new EventEmitter<NxRadioChange>();
  @Output() immobilieUebernehmen = new EventEmitter();
  @Output() closeImmoAuswahl = new EventEmitter();

  @select(selectImmobilieRequestIsInProgress) immobilieRequestIsInProgress$: Observable<boolean>;
  @select(selectRequestFailed) immobilieRequestFailed$: Observable<boolean>;
  @select(selectIsAuslandsImmobilie) isAuslandsImmobilie$: Observable<boolean>;

  subscription: Subscription;
  isOpen = false;
  modalRef: NxModalRef<unknown>;
  modalSub: Subscription;
  submit = false;

  constructor() {}

  ngOnDestroy(): void {
    if (this.modalSub) {
      this.modalSub.unsubscribe();
    }
    this.submit = false;
  }

  private showModal(value: boolean): boolean {
    const modalOpen = this.modalRef?.getState() === NxModalState.OPEN;

    if (!modalOpen && value) {
      this.modalRef = this.dialogService.open(this.templateRef, this.getModalConfig());
      this.modalSub = this.modalRef.afterClosed().subscribe(() => {
        this.afterClose();
      });
    } else if (modalOpen && !value && this.modalRef) {
      this.modalRef.close();
    }
    return value;
  }

  immobilieRadioClicked(event: NxRadioChange): void {
    this.immobilieAusgewaehlt.emit(event);
  }

  neueImmobilieRadioClicked(event: NxRadioChange): void {
    this.neueImmobilieAusgewaehlt.emit(event);
  }

  uebernehmenClicked(): void {
    this.immobilieUebernehmen.emit();
    this.submit = !selectImmobilieAuswahlErrorExists;
  }

  afterClose(): void {
    if (!this.submit) {
      this.closeImmoAuswahl.emit();
    }
  }

  showInvalidImmobilieMessage(): ImmobilieFormatted {
    return this.immobilien.find(immo => immo.disabled);
  }

  trackByIndexAndId(index: number, item: ImmobilieFormatted): string {
    return `${index}_${item && item.immoId}`;
  }

  getValidImmobilien(): ImmobilieFormatted[] {
    return this.immobilien.filter(immobilie => immobilie.disabled === false);
  }

  getInvalidImmobilien(): ImmobilieFormatted[] {
    return this.immobilien.filter(
      immobilie => immobilie.disabled === true && immobilie.usedInOtherAngebot === false,
    );
  }

  getExistentImmobilien(): ImmobilieFormatted[] {
    return this.immobilien.filter(
      immobilie => immobilie.disabled === true && immobilie.usedInOtherAngebot,
    );
  }

  shouldShowImmobilienRadioToggle(immobilieRequestIsInProgress?: boolean): boolean {
    return !!(immobilieRequestIsInProgress === false || null);
  }

  shouldShowImmobilienAuswahlInfoText(
    text: ImmobilienAuswahlModalText,
    immobilieRequestFailed?: boolean,
  ): boolean {
    return !!(text.infoMessage && (immobilieRequestFailed === false || null || undefined));
  }

  shouldShowNoImmobilienInfoText(
    immobilien: ImmobilieFormatted[],
    immobilieRequestIsInProgress?: boolean,
  ): boolean {
    return !!(
      immobilien.length === 0 &&
      (immobilieRequestIsInProgress === false || null || undefined)
    );
  }

  shouldShowImmobilienErrorMessage(
    immobilien: ImmobilieFormatted[],
    immobilieRequestIsInProgress?: boolean,
    immobilieRequestFailed?: boolean,
  ): boolean {
    return !!(
      immobilien.length === 0 &&
      (immobilieRequestIsInProgress === false || null || undefined) &&
      (immobilieRequestFailed === false || null || undefined)
    );
  }

  isAuslandsImmobilieAllowed(): boolean {
    return this.kooperation === DIPLOMATEN_CODE && areAppTypesEquivalent(this.openForApp, App.VHV);
  }

  private getModalConfig(): NxModalConfig {
    return {
      disableClose: true,
      showCloseIcon: true,
      autoFocus: true,
      ariaLabel: this.text?.ariaLabel || DEFAULT_IMMO_MODAL_ARIA_LABEL,
      closeIconButtonLabel: this.text?.closeButtonLabel || DEFAULT_IMMO_MODAL_CLOSE_BUTTON_LABEL,
      maxHeight: 'calc(100vh - 48px)',
      maxWidth: 'calc(100vw - 48px)',
    };
  }
}
